import { isConditionComplete, isLegacyCondition } from "./conditions";
import { LOGIC, type Logic } from "./constants";
import type { FormCondition, FormRule } from "./schemas";

import { OperatorValue } from "@/config/operatorConfig";
import type { OverlapGroup, SegmentField } from "@/features/segments/types";

const MAX_CONDITION_VALUES = 50;

type Member = { rule: FormRule; condition: FormCondition };

const foldAscii = (value: string): string =>
  value.replace(/[a-z]/g, (letter) => letter.toUpperCase());

const mergeableInversion = (type: FormRule["type"]): boolean | undefined => {
  switch (type) {
  case LOGIC.OR:
    return false;
  case LOGIC.AND:
    return true;
  default:
    return undefined;
  }
};

const oppositeLogic = (type: Logic): Logic =>
  type === LOGIC.AND ? LOGIC.OR : LOGIC.AND;

const isFlat = (rule: FormRule, segmentType: Logic | undefined): boolean => {
  if (rule.type === undefined || segmentType === undefined) return false;
  if (rule.conditions.length === 1) return true;
  const connective = rule.isInverted ? oppositeLogic(rule.type) : rule.type;
  return connective === segmentType;
};

const isEligible = (
  condition: FormCondition,
  fields: SegmentField[],
): boolean =>
  condition.operator === OperatorValue.IS_ANY_OF &&
  isConditionComplete(condition) &&
  !isLegacyCondition(condition, fields);

const dedupeValues = (conditions: FormCondition[]): string[] => {
  const seen = new Set<string>();
  return conditions
    .flatMap((condition) => condition.values)
    .filter((value) => {
      const key = foldAscii(value);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
};

const toChunks = (values: string[]): string[][] =>
  Array.from(
    { length: Math.ceil(values.length / MAX_CONDITION_VALUES) },
    (_, index) =>
      values.slice(
        index * MAX_CONDITION_VALUES,
        (index + 1) * MAX_CONDITION_VALUES,
      ),
  );

const groupByField = <T>(
  items: T[],
  fieldOf: (item: T) => string,
): Map<string, T[]> => {
  const grouped = new Map<string, T[]>();
  for (const item of items) {
    const field = fieldOf(item);
    grouped.set(field, [...(grouped.get(field) ?? []), item]);
  }
  return grouped;
};

const toGroups = (
  key: string,
  field: string,
  isNegative: boolean,
  members: Member[],
): OverlapGroup[] => {
  const valueChunks = toChunks(
    dedupeValues(members.map(({ condition }) => condition)),
  );
  if (valueChunks.length >= members.length) return [];
  return [
    {
      key,
      field,
      isNegative,
      members: members.map(({ rule, condition }) => ({
        ruleKey: rule.clientKey,
        conditionKey: condition.clientKey,
      })),
      valueChunks,
    },
  ];
};

const findSegmentGroups = (
  rules: FormRule[],
  segmentType: Logic | undefined,
  fields: SegmentField[],
): OverlapGroup[] => {
  const isNegative = mergeableInversion(segmentType);
  if (isNegative === undefined) return [];
  const literals = rules
    .filter((rule) => isFlat(rule, segmentType))
    .flatMap((rule) =>
      rule.conditions
        .filter(
          (condition) =>
            isEligible(condition, fields) &&
            (condition.isInverted !== rule.isInverted) === isNegative,
        )
        .map((condition) => ({ rule, condition })),
    );
  return [...groupByField(literals, ({ condition }) => condition.field)].flatMap(
    ([field, members]) =>
      toGroups(
        `segment:${field}:${isNegative ? "negative" : "positive"}`,
        field,
        isNegative,
        members,
      ),
  );
};

const findRuleGroups = (
  rule: FormRule,
  segmentType: Logic | undefined,
  fields: SegmentField[],
): OverlapGroup[] => {
  const isInverted = mergeableInversion(rule.type);
  if (isInverted === undefined || isFlat(rule, segmentType)) return [];
  const mergeable = rule.conditions.filter(
    (condition) =>
      condition.isInverted === isInverted && isEligible(condition, fields),
  );
  return [...groupByField(mergeable, (condition) => condition.field)].flatMap(
    ([field, conditions]) =>
      toGroups(
        `rule:${rule.clientKey}:${field}`,
        field,
        isInverted,
        conditions.map((condition) => ({ rule, condition })),
      ),
  );
};

export const findOverlapGroups = (
  rules: FormRule[],
  segmentType: Logic | undefined,
  fields: SegmentField[],
): OverlapGroup[] => [
  ...findSegmentGroups(rules, segmentType, fields),
  ...rules.flatMap((rule) => findRuleGroups(rule, segmentType, fields)),
];

export const ruleKeysOf = (group: OverlapGroup): FormRule["clientKey"][] => [
  ...new Set(group.members.map(({ ruleKey }) => ruleKey)),
];

export const groupByRule = (groups: OverlapGroup[]) => {
  const groupsByRule = new Map<FormRule["clientKey"], OverlapGroup[]>();
  for (const group of groups) {
    const [ruleKey, ...others] = ruleKeysOf(group);
    if (others.length > 0 || !ruleKey) continue;
    groupsByRule.set(ruleKey, [...(groupsByRule.get(ruleKey) ?? []), group]);
  }
  return groupsByRule;
};

export const mergeOverlapGroups = (
  rules: FormRule[],
  groups: OverlapGroup[],
): {
  conditionsByRule: Map<FormRule["clientKey"], FormCondition[]>;
  removedRules: FormRule[];
  removedIndexes: number[];
  survivorKeys: FormCondition["clientKey"][];
} => {
  const valuesByCondition = new Map<string, string[] | undefined>(
    groups.flatMap((group) =>
      group.members.map(
        ({ conditionKey }, index) =>
          [conditionKey, group.valueChunks[index]] as const,
      ),
    ),
  );
  const conditionsByRule = new Map<FormRule["clientKey"], FormCondition[]>();
  const removedRules: FormRule[] = [];
  const removedIndexes: number[] = [];
  const survivorKeys: FormCondition["clientKey"][] = [];
  rules.forEach((rule, ruleIndex) => {
    const isTouched = rule.conditions.some(({ clientKey }) =>
      valuesByCondition.has(clientKey),
    );
    if (!isTouched) return;
    const conditions = rule.conditions.flatMap((condition) => {
      if (!valuesByCondition.has(condition.clientKey)) return [condition];
      const values = valuesByCondition.get(condition.clientKey);
      if (!values) return [];
      survivorKeys.push(condition.clientKey);
      return [{ ...condition, values }];
    });
    if (conditions.length > 0) {
      conditionsByRule.set(rule.clientKey, conditions);
      return;
    }
    removedRules.push(rule);
    removedIndexes.unshift(ruleIndex);
  });
  return { conditionsByRule, removedRules, removedIndexes, survivorKeys };
};
